import React, { useMemo, useRef, useCallback } from "react";
import { InfiniteQueryResult, QueryKey } from "react-query";
import { useTranslation } from "react-i18next";
import { AgGridReactProps } from "@ag-grid-community/react";
import { GridApi, RowNode } from "@ag-grid-community/core";

import { DataGrid } from "fe-shared/src/components";
import InvoiceAdditionalFilters from "./components/InvoiceAdditionalFilters/InvoiceAdditionalFilters";
import ExportExcelData from "fe-shared/src/components/ExportExcelData";
import {
  getInvoicesRequestParams,
  getFlattenedInvoices,
} from "fe-shared-transactions/src/helpers/invoicesRequestConverter";
import useInvoicesExport from "fe-shared-transactions/src/hooks/useInvoicesExport";
import {
  DataGridFilters,
  DataGridOptions,
} from "fe-shared/src/components/DataGrid/modules/types";

import { GridInvoiceList, GridInvoice } from "fe-shared-transactions/src/types";

import { getColumnDefs, getRenderers } from "./modules/config";

import styles from "./InvoicesDataGrid.module.scss";

interface Props extends AgGridReactProps {
  invoices: InfiniteQueryResult<GridInvoiceList<GridInvoice>>;
  lastOpenedInvoiceNumber: string | null;
  clearLastOpenedInvoiceNumber: () => void;
  options: DataGridOptions;
  filters: DataGridFilters;
  setOptions: (options: DataGridOptions) => void;
  setFilters: (filters: DataGridFilters) => void;
  canExport?: boolean;
  canAssign?: boolean;
  invoicesQueryKey: QueryKey;
}

const InvoicesDataGrid = (props: Props) => {
  const {
    lastOpenedInvoiceNumber,
    clearLastOpenedInvoiceNumber,
    invoices,
    filters,
    canExport,
    canAssign = false,
    invoicesQueryKey,
    ...otherProps
  } = props;

  const { t } = useTranslation();
  const gridApiRef = useRef<GridApi | null>(null);
  const columnDefs = useMemo(() => getColumnDefs(t), [t]);
  const requestParams = getInvoicesRequestParams(filters, otherProps.options);
  const { getDataRows, columnConfig } = useInvoicesExport(requestParams);

  const assignGridApi = (api: GridApi) => {
    gridApiRef.current = api;
  };

  const onFirstDataRendered = useCallback(() => {
    if (lastOpenedInvoiceNumber) {
      gridApiRef.current!.ensureNodeVisible(
        (node: RowNode) => node.id === lastOpenedInvoiceNumber.toString(),
        "middle"
      );

      clearLastOpenedInvoiceNumber();
    }
  }, [lastOpenedInvoiceNumber, clearLastOpenedInvoiceNumber]);

  const renderers = useMemo(() => getRenderers(canAssign, invoicesQueryKey), [
    canAssign,
    invoicesQueryKey,
  ]);

  const rowCount = invoices.data?.[0].total;

  const invoicesList = useMemo(() => getFlattenedInvoices(invoices.data), [
    invoices,
  ]);

  const updateFilters = (filtersUpdate: DataGridFilters) => {
    gridApiRef.current?.setFilterModel({
      ...filters,
      ...filtersUpdate,
    });
  };

  return (
    <main className={styles.container}>
      <div className={styles.headerBlock}>
        <h1 className={styles.header}>
          {t("invoices.grid.title")}
          {!!rowCount && <span className={styles.totalCount}>{rowCount}</span>}
        </h1>
        <div className={styles.buttons}>
          {canExport && (
            <ExportExcelData
              dataRows={[]}
              getDataRows={getDataRows}
              columnConfig={columnConfig}
              buttonId="export-invoices-button"
              fileName={t("invoices.grid.export.fileName")}
              buttonLabel={t("invoices.grid.export.buttonLabel")}
            />
          )}
          <InvoiceAdditionalFilters
            filters={filters}
            updateFilters={updateFilters} />
        </div>
      </div>
      <DataGrid
        idField="invoiceId"
        isLoading={invoices.isLoading}
        assignGridApi={assignGridApi}
        columnDefs={columnDefs}
        onFirstDataRendered={onFirstDataRendered}
        rowData={invoicesList}
        fetchMore={invoices.fetchMore}
        canFetchMore={invoices.canFetchMore}
        isFetchingMore={invoices.isFetchingMore}
        className={styles.invoicesDataGrid}
        filters={filters}
        frameworkComponents={renderers}
        {...otherProps}
      />
    </main>
  );
};

export default InvoicesDataGrid;
